(() => {
  window.KBSelectionCards = class {
    constructor({ root, cards, field, chosen, create, sync, update, editors, text, money, config, productFor, thumbnail, busy, canChange, onOpen }) {
      Object.assign(this, { root, cards, field, chosen, create, sync, update, editors, text, money, config, productFor, thumbnail, busy, canChange, onOpen });
      this.active = null;
      this.identity = new WeakMap();
      this.editing = new WeakSet();
      this.identityFields = ['kind', 'catalog-product', 'weight', 'color'];
      this.notice = root.querySelector('[data-selection-status]');
    }
    state(card) {
      return Object.fromEntries(this.identityFields.map(name => [name, this.field(card, name)?.value]).filter(([, value]) => value !== undefined));
    }
    remember(card) { this.identity.set(card, this.state(card)); }
    hasQuantity(card) { return this.chosen(card).some(row => row.quantity !== 0 || !Number.isSafeInteger(row.quantity)); }
    hasFilesOrNotes(card) { return ['front', 'back', 'label'].some(side => this.field(card, side)?.files.length) || Boolean(this.field(card, 'notes')?.value.trim()); }
    empty(card) { return !this.hasQuantity(card) && !this.hasFilesOrNotes(card); }
    announce(key) { this.notice.textContent = this.text(key); }
    attach(card, id) {
      const panel = card.querySelector('.kb-order-card__grid');
      panel.id = `${id}-editor`;
      const edit = this.field(card, 'edit');
      edit.setAttribute('aria-controls', panel.id);
      edit.addEventListener('click', () => { if (!this.busy()) { this.editing.add(card); this.open(card, true); } });
      card.addEventListener('invalid', () => this.open(card), true);
      card.addEventListener('kb:artwork-rendered', () => this.renderThumbnail(card));
      this.remember(card);
      this.open(card);
    }
    open(card, focus = false) {
      this.active = card;
      for (const other of this.cards.children) {
        const active = other === card;
        other.classList.toggle('is-collapsed', !active);
        other.querySelector('.kb-order-card__grid').hidden = !active;
        this.field(other, 'edit').hidden = active;
        this.field(other, 'edit').setAttribute('aria-expanded', String(active));
      }
      this.onOpen?.(card);
      if (focus) {
        card.scrollIntoView({ behavior: 'instant', block: 'start' });
        card.querySelector('[data-size]:not(:disabled)')?.focus({ preventScroll: true });
      }
    }
    draft(state, source) {
      const empty = [...this.cards.children].find(card => card !== source && this.empty(card));
      const card = empty || this.create(state.kind, state['catalog-product']);
      this.editing.delete(card);
      for (const [name, value] of Object.entries(state)) if (this.field(card, name)) this.field(card, name).value = value;
      if (source && this.field(source, 'layout')) this.field(card, 'layout').value = this.field(source, 'layout').value;
      if (state.kind === 'catalog') card.dataset.catalogChosen = String(Boolean(state['catalog-product']));
      this.open(card); this.sync(card);
      return card;
    }
    another() {
      if (this.busy()) return null;
      const source = this.active || this.cards.lastElementChild;
      if (source) this.editing.delete(source);
      if (source && !this.hasQuantity(source)) {
        this.open(source);
        source.querySelector('[data-size]:not(:disabled)')?.focus();
        this.announce('variant_quantity_first');
        return source;
      }
      const card = this.draft(source ? this.state(source) : { kind: this.root.dataset.defaultKind }, source);
      this.announce('variant_added');
      card.scrollIntoView({ behavior: 'instant', block: 'start' });
      this.field(card, 'color')?.focus({ preventScroll: true });
      return card;
    }
    change(card, changes) {
      const before = this.identity.get(card) || this.state(card);
      const after = { ...this.state(card), ...changes };
      const changed = Object.keys(after).some(name => after[name] !== before[name]);
      if (changed && this.editing.has(card)) {
        for (const [name, value] of Object.entries(after)) this.field(card, name).value = value;
        const blockedType = after.kind !== before.kind && this.hasFilesOrNotes(card);
        if (blockedType || !this.canChange(card)) {
          for (const [name, value] of Object.entries(before)) this.field(card, name).value = value;
          this.sync(card);
          this.announce(blockedType ? 'variant_art_change' : 'variant_invalid_change');
          return card;
        }
        this.sync(card);
        return card;
      }
      if (changed && (this.hasQuantity(card) || after.kind !== before.kind && this.hasFilesOrNotes(card))) {
        for (const [name, value] of Object.entries(before)) this.field(card, name).value = value;
        this.sync(card);
        const draft = this.draft(after, card);
        this.announce('variant_preserved');
        draft.scrollIntoView({ behavior: 'instant', block: 'start' });
        const changedName = Object.keys(after).find(name => after[name] !== before[name]);
        this.field(draft, changedName === 'catalog-product' ? 'color' : changedName)?.focus({ preventScroll: true });
        return draft;
      }
      for (const [name, value] of Object.entries(after)) this.field(card, name).value = value;
      this.open(card); this.sync(card);
      return card;
    }
    remove(card) {
      if (this.busy()) return;
      const next = card.nextElementSibling || card.previousElementSibling;
      this.editors.get(card)?.dispose();
      card.remove();
      if (next) this.open(this.active === card ? next : this.active || next);
      else this.create(this.root.dataset.defaultKind);
      this.update();
      (this.active?.querySelector('[data-size]:not(:disabled)') || this.root.querySelector('[data-another]'))?.focus({ preventScroll: true });
      this.announce('variant_removed');
    }
    render(card, { selected, comboTotal, comboRetail, priceRange }) {
      const kind = this.field(card, 'kind').value;
      const color = this.field(card, 'color').value;
      const weight = this.field(card, 'weight').value;
      const title = kind === 'catalog' ? this.productFor(card)?.title || this.text('catalog') : this.text(kind);
      const translatedColor = this.text(color === 'Negro' ? 'black' : 'white');
      this.field(card, 'selection-name').textContent = title;
      this.field(card, 'selection-options').textContent = `${translatedColor} · ${weight} g`;
      this.field(card, 'selection-swatch').classList.toggle('is-black', color === 'Negro');
      this.field(card, 'selection-quantities').textContent = selected.length ? selected.map(row => `${row.input.dataset.size} × ${row.quantity}`).join(' · ') : this.text('variant_empty');
      this.field(card, 'selection-unit').textContent = this.text('unit', { price: priceRange('unit') });
      this.field(card, 'selection-total').textContent = this.money(comboTotal);
      this.field(card, 'selection-retail').textContent = this.money(comboRetail);
      this.field(card, 'selection-retail').hidden = comboRetail <= comboTotal;
      this.field(card, 'selection-saving').textContent = this.text('saving', { price: this.money(Math.max(0, comboRetail - comboTotal)) });
      this.field(card, 'selection-saving').hidden = comboRetail <= comboTotal;
      const files = ['front', 'back', 'label'].map(side => this.field(card, side)?.files[0]?.name).filter(Boolean);
      const fileSummary = this.field(card, 'selection-files');
      fileSummary.textContent = files.join(' · '); fileSummary.hidden = !files.length;
      this.field(card, 'edit').setAttribute('aria-label', `${this.text('variant_edit')}: ${title} · ${translatedColor} · ${weight} g`);
      this.renderThumbnail(card);
    }
    renderThumbnail(card) {
      const kind = this.field(card, 'kind').value;
      const color = this.field(card, 'color').value;
      const weight = this.field(card, 'weight').value;
      const title = kind === 'catalog' ? this.productFor(card)?.title || this.text('catalog') : this.text(kind);
      const translatedColor = this.text(color === 'Negro' ? 'black' : 'white');
      const image = this.field(card, 'selection-image'), canvas = this.field(card, 'selection-canvas');
      const editor = this.editors.get(card);
      const side = editor?.side === 'back' ? 'back' : 'front';
      const label = `${title} · ${translatedColor} · ${weight} g · ${this.text(`face_${side}`)}`;
      image.alt = label; canvas.setAttribute('aria-label', label);
      const src = this.thumbnail?.(card, side) || (kind !== 'catalog' ? this.config.images[(color === 'Negro' ? 'black' : 'white') + (side === 'back' ? 'Back' : 'Front')] : null);
      if (src && image.getAttribute('src') !== src) image.src = src;
      if (!src) image.removeAttribute('src');
      image.hidden = !src; canvas.hidden = true;
      const token = card.dataset.thumbnailToken = String((Number(card.dataset.thumbnailToken) || 0) + 1);
      if (kind !== 'catalog' && editor?.draw) Promise.all([editor.shirtReady, ...Object.values(editor.states || {}).map(state => state.pending)]).then(() => {
        if (!card.isConnected || card.dataset.thumbnailToken !== token || !editor.shirtSet?.[side]) return;
        try { editor.draw(canvas.getContext('2d'), side, canvas.width); canvas.hidden = false; image.hidden = true; } catch (_) { }
      });
    }
  };
})();
